// utils/validation.js

import { toast } from "./toast";
import { filterAvailableRides, filterAvailableShows } from "./availabilityRules";

// Human-friendly field labels
const friendlyLabelMap = {
  fullName: "Your full name",
  email: "Your email address",
  phoneNumber: "Your phone number",
  startDate: "Trip start date",
  endDate: "Trip end date",
  adults: "Number of adults",
  numAdultMales: "Number of adult males",
  numAdultFemales: "Number of adult females",
  kids: "Number of children",
  numBoys: "Number of boys",
  numGirls: "Number of girls",
  agesOfBoys: "Ages of boys",
  agesOfGirls: "Ages of girls",
  ticketType: "Your Disney ticket type",
  hasParkHopper: "Park Hopper or Annual Pass option",
  parks: "Selected parks",
  mkDays: "Magic Kingdom day count",
  hsDays: "Hollywood Studios day count",
  epcotDays: "EPCOT day count",
  akDays: "Animal Kingdom day count",
  tlDays: "Typhoon Lagoon day count",
  bbDays: "Blizzard Beach day count",
  stayingOnProperty: "Resort stay preference",
  resort: "Disney resort name",
  offPropertyHotel: "Off-property hotel name",
  otherHotel: "Specific off-property hotel",
  rideStrategy: "Your ride strategy",
  lightningLane: "Lightning Lane preference",
  mustRides: "Your top ride selections",
  showStrategy: "Show & entertainment interests",
  mustSeeShows: "Must-see shows",
  diningPlanInterest: "Interest in dining plan",
  dining: "Dining preferences",
  mustTryRestaurants: "Your restaurant selections",
  diningTime: "Preferred dining time",
  cuisinePreferences: "Cuisine preferences",
  allergies: "Food allergies",
  parkStartTime: "Preferred park entry time",
  breakTime: "Midday break plans",
  celebration: "Whether you're celebrating anything special",
  celebrationDetails: "What you're celebrating",
  celebrationDate: "Date of the celebration",
  hasDisability: "Disability or mobility concerns",
  disabilities: "Disability notes",
  additionalInfo: "Additional trip notes",
  transportation: "Transportation method",
  restaurantStrategy: "Your restaurant preferences",
  enchantedExperiences: "Interest in enchanted experiences",
  mustExperience: "Premium experience selections",
};

// Optional field IDs
const optionalIds = [
  "agesOfBoys",
  "agesOfGirls",
  "additionalInfo",
  "otherRides",
  "endDate",
  "disabilities",
  "otherHotel",
];

// Utility
const isEmpty = (val) =>
  val === undefined ||
  val === null ||
  (typeof val === "string" && val.trim() === "") ||
  (Array.isArray(val) && val.length === 0);

// Park mapping
const parkDayFields = {
  mkDays: "Magic Kingdom",
  epcotDays: "EPCOT",
  hsDays: "Hollywood Studios",
  akDays: "Animal Kingdom",
  tlDays: "Typhoon Lagoon",
  bbDays: "Blizzard Beach",
};

// === Main Validation Function ===
export const validateField = (field, formData) => {
  if (!formData || typeof formData !== "object") return false;

  // Helper function to get value from nested form data structure
  const getFieldValue = (fieldId) => {
    // Map field IDs to their sections
    const fieldToSection = {
      // Basic Information
      fullName: 'basicInformation',
      email: 'basicInformation', 
      phoneNumber: 'basicInformation',
      startDate: 'basicInformation',
      endDate: 'basicInformation',
      
      // Group Information
      adults: 'groupInfo',
      numAdultMales: 'groupInfo',
      numAdultFemales: 'groupInfo',
      kids: 'groupInfo',
      numBoys: 'groupInfo',
      numGirls: 'groupInfo',
      agesOfBoys: 'groupInfo',
      agesOfGirls: 'groupInfo',
      
      // Ticket & Park Access
      ticketType: 'parkTicketAccess',
      hasParkHopper: 'parkTicketAccess',
      
      // Park Selection
      parks: 'parkVisitPlans',
      mkDays: 'parkVisitPlans',
      hsDays: 'parkVisitPlans',
      epcotDays: 'parkVisitPlans',
      akDays: 'parkVisitPlans',
      tlDays: 'parkVisitPlans',
      bbDays: 'parkVisitPlans',
      includeRestDays: 'parkVisitPlans',
      restDayPlacement: 'parkVisitPlans',
      restDayStyle: 'parkVisitPlans',
      restDayCount: 'parkVisitPlans',
      
      // Accommodation & Transportation
      transportation: 'accommodation',
      stayingOnProperty: 'accommodation',
      resort: 'accommodation',
      otherHotel: 'accommodation',
      
      // Attraction & Ride Preferences
      lightningLane: 'attractionRideStrategy',
      rideStrategy: 'attractionRideStrategy',
      mustRides: 'attractionRideStrategy',
      
      // Shows & Firework Preferences
      showStrategy: 'showFireworkStrategy',
      mustSeeShows: 'showFireworkStrategy',
      
      // Dining Preferences
      diningPlanInterest: 'diningPreferences',
      restaurantStrategy: 'diningPreferences',
      mustTryRestaurants: 'diningPreferences',
      diningTime: 'diningPreferences',
      allergies: 'diningPreferences',
      
      // Budget Preferences
      budget: 'budget',
      budgetLabel: 'budget',
      budgetMin: 'budget',
      budgetMax: 'budget',
      
      // Time & Energy Preferences
      parkStartTime: 'timeEnergy',
      breakTime: 'timeEnergy',
      
      // Special Considerations
      celebration: 'specialConsiderations',
      celebrationDetails: 'specialConsiderations',
      celebrationDate: 'specialConsiderations',
      hasDisability: 'specialConsiderations',
      disabilities: 'specialConsiderations',
      additionalInfo: 'specialConsiderations',
      
      // Specialty/Premium Experiences
      enchantedExperiences: 'specialtyExperiences',
      mustExperience: 'specialtyExperiences'
    };

    const section = fieldToSection[fieldId];
    
    if (section && formData[section]) {
      return formData[section][fieldId] || "";
    }
    
    // Fallback to flat structure for unknown fields
    return formData[fieldId] || "";
  };

  const value = getFieldValue(field.id);

  // Skip info-only fields
  if (field.type === "info") return true;

  // Skip optional fields
  if (optionalIds.includes(field.id)) return true;

  // === CHILD VALIDATION ===
  const totalKids = parseInt(getFieldValue('kids') || "0", 10);
  if (
    totalKids === 0 &&
    ["numBoys", "numGirls", "agesOfBoys", "agesOfGirls"].includes(field.id)
  ) {
    return true;
  }

  // === ADULT VALIDATION ===
  const totalAdults = parseInt(getFieldValue('adults') || "0", 10);
  if (
    totalAdults === 0 &&
    ["numAdultMales", "numAdultFemales"].includes(field.id)
  ) {
    return true;
  }

  // === PARK DAYS ===
  if (parkDayFields[field.id]) {
    const selectedParks = getFieldValue('parks') || [];
    const parkName = parkDayFields[field.id];

    // Only validate if the park was selected
    if (selectedParks.includes(parkName)) {
      const parsed = parseInt(value || "0", 10);
      if (isNaN(parsed) || parsed < 1) {
        toast.error(`Please enter at least 1 day for ${parkName}.`);
        return false;
      }
    }

    return true; // if not selected, skip validation
  }

  // === STAYING ON PROPERTY LOGIC ===
  if (field.id === "resort") {
    if (getFieldValue('stayingOnProperty') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please select your Disney resort.");
      return false;
    }
  }

  if (field.id === "offPropertyHotel") {
    if (getFieldValue('stayingOnProperty') !== "No") return true;
    if (isEmpty(value)) {
      toast.error("Please enter your off-property hotel.");
      return false;
    }
  }

  if (field.id === "otherHotel") {
    const stayingOffProperty = getFieldValue('stayingOnProperty') === "No";
    const selectedOther = getFieldValue('offPropertyHotel') === "Other";
    if (!(stayingOffProperty && selectedOther)) return true;
    if (isEmpty(value)) {
      toast.error("Please enter your specific off-property hotel.");
      return false;
    }
  }

  // === CELEBRATION DETAILS (dropdown) ===
  if (field.id === "celebrationDetails") {
    if (getFieldValue('celebration') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please tell us what you're celebrating.");
      return false;
    }
  }

  // === CELEBRATION DATE ===
  if (field.id === "celebrationDate") {
    if (getFieldValue('celebration') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please provide the date of the celebration.");
      return false;
    }
  }

  // === DISABILITY NOTES ===
  if (field.id === "disabilities") {
    if (getFieldValue('hasDisability') !== "Yes") return true;
    if (isEmpty(value)) {
      toast.error("Please explain the disability or mobility concern.");
      return false;
    }
  }

  // === EXPERIENCE SELECTIONS ===
  if (field.id === "mustExperience") {
    const enchantedExperiences = getFieldValue('enchantedExperiences') || [];
    if (!enchantedExperiences.includes("Yes")) return true;
    if (isEmpty(value)) {
      toast.error("Please select at least one premium experience.");
      return false;
    }
  }

  // === TRANSPORTATION ===
  if (field.id === "transportation") {
    if (isEmpty(value)) {
      toast.error("Please select a mode of transportation around the parks.");
      return false;
    }
    return true;
  }


  // === DEFAULT CHECK ===
  if (isEmpty(value)) {
    const label = friendlyLabelMap[field.id] || "this question";
    toast.error(`Please complete: ${label}`);
    return false;
  }

  return true;
};

export const validateAgeDropdowns = (count, list, label) => {
  if (
    count > 0 &&
    (!Array.isArray(list) || list.length !== count || list.includes(""))
  ) {
    toast.error(`Please select the age of each ${label}.`);
    return false;
  }
  return true;
};

export const validateAdultSplit = (formData) => {
  const total = parseInt(formData.groupInfo?.adults || "0");
  const males = parseInt(formData.groupInfo?.numAdultMales || "0");
  const females = parseInt(formData.groupInfo?.numAdultFemales || "0");
  if (males + females !== total) {
    toast.error("Number of adult males and females must equal total adults.");
    return false;
  }
  return true;
};

// ============================================================================
// NOTE: We don't block the form if children distribution doesn't match totalKids.
// Families may enter approximate values (e.g., prefer not to specify gender breakdown,
// have non-binary children, or just provide rough numbers). We treat this as heuristic
// data for planning purposes only. This avoids blocking real families with valid use cases.
// ============================================================================
export const validateKidSplit = (formData) => {
  const total = parseInt(formData.groupInfo?.kids || "0");
  const boys = parseInt(formData.groupInfo?.numBoys || "0");
  const girls = parseInt(formData.groupInfo?.numGirls || "0");
  
  if (total > 0 && boys + girls !== total && (boys > 0 || girls > 0)) {
    // Log warning for analytics/debugging (no PII)
    console.warn('[Questionnaire] Children distribution approximate', {
      total,
      boys,
      girls,
      difference: total - (boys + girls)
    });
    
    // Show informational toast (non-blocking, soft warning)
    toast.info("Note: We'll use approximate age information for planning. Your itinerary will still be personalized!", {
      autoClose: 5000
    });
  }
  
  // Always return true - never block form submission
  return true;
};

export const validateRideSelection = (rides, formData) => {
  const list = Array.isArray(rides) ? rides : [];
  const { filtered, removed } = filterAvailableRides(list, formData || {});

  if (removed.length > 0) {
    return {
      isValid: true,
      filteredRides: filtered,
      message: "Some selections were removed because they're not available for your trip dates."
    };
  }
  if (filtered.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one ride to continue."
    };
  }
  return { isValid: true };
};

export const validateShowSelection = (shows, optedOut, formData) => {
  if (optedOut) {
    return { isValid: true };
  }
  const list = Array.isArray(shows) ? shows : [];
  const { filtered, removed } = filterAvailableShows(list, formData || {});

  if (removed.length > 0) {
    return {
      isValid: true,
      filteredShows: filtered,
      message: "Some selections were removed because they're not available for your trip dates."
    };
  }
  if (filtered.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one show or choose 'Not Interested' to continue."
    };
  }
  return { isValid: true };
};

export const validateCelebrationDetails = (celebration, details) => {
  if (celebration === "Yes" && isEmpty(details)) {
    toast.error("Please tell us what you're celebrating.");
    return false;
  }
  return true;
};

export const validateDisabilityNotes = (hasDisability, note) => {
  if (hasDisability === "Yes" && isEmpty(note)) {
    toast.error("Please explain the disability or mobility concern.");
    return false;
  }
  return true;
};

/**
 * Validates rest-day questions in parkVisitPlans.
 * - includeRestDays is required (must be "Yes" or "No").
 * - If "Yes", restDayCount, restDayPlacement, and restDayStyle are required.
 * @param {Object} formData - Form data with parkVisitPlans
 * @returns {{ isValid: boolean, message?: string }}
 */
export const validateRestDays = (formData) => {
  const pvp = formData?.parkVisitPlans || {};
  const includeRest = (pvp.includeRestDays || "").trim();

  if (includeRest !== "Yes" && includeRest !== "No") {
    return { isValid: false, message: "Please answer whether you would like to include Rest days in your trip." };
  }

  if (includeRest === "No") {
    return { isValid: true };
  }

  if (!(pvp.restDayCount || "").trim()) {
    return { isValid: false, message: "Please select how many rest days you would like." };
  }
  if (!(pvp.restDayPlacement || "").trim()) {
    return { isValid: false, message: "Please select where you would like your Rest day(s) to fall." };
  }
  if (!(pvp.restDayStyle || "").trim()) {
    return { isValid: false, message: "Please select what a Rest day(s) should look like." };
  }

  return { isValid: true };
};

export const validateRestaurantSelection = (restaurants, optedOut) => {
  if (optedOut) {
    return { isValid: true };
  }

  if (!restaurants || restaurants.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one restaurant you'd like to try."
    };
  }

  if (restaurants.length > 20) {
    return {
      isValid: false,
      message: "Please select no more than 20 restaurants."
    };
  }

  return { isValid: true };
};

export const validateExperienceSelection = (experiences, interested) => {
  if (!interested || !interested.includes("Yes")) {
    return { isValid: true };
  }

  if (!experiences || experiences.length === 0) {
    return {
      isValid: false,
      message: "Please select at least one premium experience you'd like to try."
    };
  }

  if (experiences.length > 15) {
    return {
      isValid: false,
      message: "Please select no more than 15 premium experiences."
    };
  }

  return { isValid: true };
};
// ─── Questionnaire data pipeline validators (merged from dataValidation.js) ──
// These were previously in src/utils/dataValidation.js.
// Import from this file going forward: import { validateFormData } from './validation'

import { parseDateForCompare, startOfLocalDay } from './dateUtils';

/**
 * Validate and sanitize questionnaire data before submission.
 * Handles both flat and organised (nested-section) data shapes.
 */
export const validateQuestionnaireData = (formData) => {
  const errors = [];
  const warnings = [];

  const getVal = (data, field) => {
    if (data.basicInformation || data.parkVisitPlans || data.attractionRideStrategy) {
      const map = {
        fullName: data.basicInformation?.fullName,
        email: data.basicInformation?.email,
        phoneNumber: data.basicInformation?.phoneNumber,
        startDate: data.basicInformation?.startDate,
        endDate: data.basicInformation?.endDate,
        adults: data.groupInfo?.adults,
        kids: data.groupInfo?.kids,
        numBoys: data.groupInfo?.numBoys,
        numGirls: data.groupInfo?.numGirls,
        agesOfBoys: data.groupInfo?.agesOfBoys,
        agesOfGirls: data.groupInfo?.agesOfGirls,
        parks: data.parkVisitPlans?.parks,
        mkDays: data.parkVisitPlans?.mkDays,
        hsDays: data.parkVisitPlans?.hsDays,
        epcotDays: data.parkVisitPlans?.epcotDays,
        akDays: data.parkVisitPlans?.akDays,
        tlDays: data.parkVisitPlans?.tlDays,
        bbDays: data.parkVisitPlans?.bbDays,
        ticketType: data.parkTicketAccess?.ticketType,
        hasParkHopper: data.parkTicketAccess?.hasParkHopper,
        rideStrategy: data.attractionRideStrategy?.rideStrategy,
        showStrategy: data.showFireworkStrategy?.showStrategy,
        mustRides: data.attractionRideStrategy?.mustRides,
        mustSeeShows: data.showFireworkStrategy?.mustSeeShows,
        mustTryRestaurants: data.diningPreferences?.mustTryRestaurants,
        allergies: data.groupInfo?.allergies,
      };
      return map[field] !== undefined ? map[field] : data[field];
    }
    return data[field];
  };

  for (const field of ['fullName', 'email', 'startDate', 'endDate', 'adults']) {
    const v = getVal(formData, field);
    if (!v || (typeof v === 'string' && !v.trim())) errors.push(`${field} is required`);
  }

  const email = getVal(formData, 'email');
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push('Invalid email format');

  const phone = getVal(formData, 'phoneNumber');
  if (phone && !/^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s\-\(\)]/g, ''))) {
    warnings.push('Phone number format may be invalid');
  }

  const startDate = getVal(formData, 'startDate');
  const endDate   = getVal(formData, 'endDate');
  if (startDate && endDate) {
    const start = parseDateForCompare(startDate);
    const end   = parseDateForCompare(endDate);
    const today = startOfLocalDay(new Date());
    if (!start || !end)  errors.push('Invalid date format');
    else if (start < today) errors.push('Start date cannot be in the past');
    else if (end < start)   errors.push('End date must be after start date');
    else if (end < today)   errors.push('End date cannot be in the past');
  }

  for (const field of ['adults', 'kids', 'numBoys', 'numGirls']) {
    const v = getVal(formData, field);
    if (v !== undefined && v !== null && v !== '') {
      const n = parseInt(v);
      if (isNaN(n) || n < 0) errors.push(`${field} must be a positive number`);
      if (n > 20)            errors.push(`${field} cannot exceed 20`);
    }
  }

  for (const ageField of ['agesOfBoys', 'agesOfGirls']) {
    const ages = getVal(formData, ageField);
    if (ages) {
      for (const age of ages) {
        const n = parseInt(age);
        if (isNaN(n) || n < 0 || n > 18) {
          errors.push(`${ageField === 'agesOfBoys' ? 'Boy' : 'Girl'} ages must be between 0 and 18`);
          break;
        }
      }
    }
  }

  let totalParkDays = 0;
  for (const field of ['mkDays', 'hsDays', 'epcotDays', 'akDays', 'tlDays', 'bbDays']) {
    const v = getVal(formData, field);
    if (v) {
      const n = parseInt(v);
      if (isNaN(n) || n < 0 || n > 10) errors.push(`${field} must be between 0 and 10`);
      else totalParkDays += n;
    }
  }

  if (startDate && endDate) {
    const tripLength = Math.floor((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
    const usableDays = tripLength - 2;
    if (totalParkDays > usableDays) {
      const ticketType  = getVal(formData, 'ticketType');
      const hopper      = getVal(formData, 'hasParkHopper');
      const canHop      = ticketType === 'Annual Pass' || ticketType === 'AP' || hopper === 'Yes';
      if (canHop) warnings.push(`${totalParkDays} park days selected for a ${tripLength}-day trip — possible with Park Hopper/Annual Pass.`);
      else        errors.push(`${totalParkDays} park days selected but only ${usableDays} days available. Consider adding Park Hopper.`);
    }
  }

  for (const field of ['parks','lightningLane','rideStrategy','mustRides','showStrategy','mustSeeShows','restaurantStrategy','mustTryRestaurants','diningTime','allergies','parkStartTime','breakTime']) {
    if (formData[field] && !Array.isArray(formData[field])) errors.push(`${field} must be an array`);
  }

  const strLimits = { fullName:100,email:100,phoneNumber:20,ticketType:100,hasParkHopper:10,transportation:100,stayingOnProperty:100,resort:100,otherHotel:100,diningPlanInterest:10,celebration:200,hasDisability:10,disabilities:500,additionalInfo:1000 };
  for (const [field, max] of Object.entries(strLimits)) {
    if (formData[field] && typeof formData[field] === 'string' && formData[field].length > max) {
      errors.push(`${field} is too long (max ${max} characters)`);
    }
  }

  if (errors.length > 0) throw new Error(`Validation failed: ${errors.join(', ')}`);
  return { data: formData, warnings: warnings.length ? warnings : null };
};

/** Validate data types with user-friendly error messages. */
export const validateDataTypesWithMessages = (data) => {
  const errors = [];
  const getVal = (d, f) => {
    if (d.basicInformation || d.parkVisitPlans || d.attractionRideStrategy) {
      const map = { fullName:d.basicInformation?.fullName, email:d.basicInformation?.email, phoneNumber:d.basicInformation?.phoneNumber, startDate:d.basicInformation?.startDate, endDate:d.basicInformation?.endDate, adults:d.groupInfo?.adults, kids:d.groupInfo?.kids, mustRides:d.attractionRideStrategy?.mustRides, mustTryRestaurants:d.diningPreferences?.mustTryRestaurants };
      return map[f] !== undefined ? map[f] : d[f];
    }
    return d[f];
  };
  const labels = { fullName:'Full Name', email:'Email Address', phoneNumber:'Phone Number', startDate:'Trip Start Date', endDate:'Trip End Date', adults:'Number of Adults', kids:'Number of Children', mustRides:'Must-Ride Attractions', mustTryRestaurants:'Must-Try Restaurants' };
  const checks = [
    {field:'fullName',type:'string',required:true},{field:'email',type:'string',required:true},
    {field:'phoneNumber',type:'string',required:true},{field:'startDate',type:'string',required:true},
    {field:'endDate',type:'string',required:true},{field:'adults',type:'number',required:true},
    {field:'kids',type:'number',required:false},{field:'mustRides',type:'array',required:true},
    {field:'mustTryRestaurants',type:'array',required:true},
  ];
  for (const { field, type, required } of checks) {
    const v = getVal(data, field);
    const label = labels[field] || field;
    if (required && (v === undefined || v === null || v === '')) {
      errors.push(`Please provide your ${label.toLowerCase()}. This information is required to create your personalised itinerary.`);
    }
    if (v !== undefined && v !== null && v !== '') {
      if (type === 'number' && typeof v !== 'number' && isNaN(parseInt(v))) errors.push(`Please enter a valid number for ${label.toLowerCase()}.`);
      if (type === 'string' && typeof v !== 'string') errors.push(`Please enter valid text for ${label.toLowerCase()}.`);
      if (type === 'array' && !Array.isArray(v)) errors.push(`Please make selections for ${label.toLowerCase()}.`);
    }
  }
  return { isValid: errors.length === 0, errors };
};

/** @returns {boolean} True if all data types are valid. */
export const validateDataTypes = (data) => validateDataTypesWithMessages(data).isValid;

/** Check for data consistency issues and return informational warnings. */
export const checkDataConsistency = (data) => {
  const warnings = [];
  const getVal = (d, f) => {
    if (d.basicInformation || d.parkVisitPlans || d.attractionRideStrategy) {
      const map = { kids:d.groupInfo?.kids, agesOfBoys:d.groupInfo?.agesOfBoys, agesOfGirls:d.groupInfo?.agesOfGirls, parks:d.parkVisitPlans?.parks, mkDays:d.parkVisitPlans?.mkDays, hsDays:d.parkVisitPlans?.hsDays, epcotDays:d.parkVisitPlans?.epcotDays, akDays:d.parkVisitPlans?.akDays, tlDays:d.parkVisitPlans?.tlDays, bbDays:d.parkVisitPlans?.bbDays, rideStrategy:d.attractionRideStrategy?.rideStrategy, showStrategy:d.showFireworkStrategy?.showStrategy, mustRides:d.attractionRideStrategy?.mustRides||d.mustRides, mustSeeShows:d.showFireworkStrategy?.mustSeeShows||d.mustSeeShows };
      return map[f] !== undefined ? map[f] : d[f];
    }
    return d[f];
  };
  const kids = getVal(data,'kids'), boys = getVal(data,'agesOfBoys'), girls = getVal(data,'agesOfGirls');
  if (kids && boys && girls) {
    const total = (boys?.length||0)+(girls?.length||0);
    if (total !== kids && total > 0) warnings.push('Children age information is approximate — itinerary will be personalised based on available data.');
  }
  const parks = getVal(data,'parks');
  if (parks?.length > 0) {
    const hasDays = ['mkDays','hsDays','epcotDays','akDays','tlDays','bbDays'].some(f => parseInt(getVal(data,f)||0) > 0);
    if (!hasDays) warnings.push('Parks selected but no park days specified.');
  }
  const rides = getVal(data,'mustRides'), rideStrat = getVal(data,'rideStrategy');
  if (rideStrat?.includes('Not Interested') && rides?.length > 0) warnings.push('Ride strategy is "Not Interested" but must-rides are selected.');
  const shows = getVal(data,'mustSeeShows'), showStrat = getVal(data,'showStrategy');
  if (showStrat?.includes('Not Interested') && shows?.length > 0) warnings.push('Show strategy is "Not Interested" but must-see shows are selected.');
  return warnings;
};

/** Returns true if data can be safely serialised to localStorage. */
export const validateForStorage = (data) => {
  try {
    JSON.stringify(data);
    const seen = new WeakSet();
    const noCircular = (obj) => {
      if (obj && typeof obj === 'object') {
        if (seen.has(obj)) return false;
        seen.add(obj);
        for (const k in obj) if (!noCircular(obj[k])) return false;
      }
      return true;
    };
    return noCircular(data);
  } catch { return false; }
};

/**
 * Comprehensive validation pipeline for questionnaire form data.
 * @returns {{ isValid: boolean, data?: object, errors: string[], warnings: string[] }}
 */
export const validateFormData = (formData) => {
  try {
    const typeResult = validateDataTypesWithMessages(formData);
    if (!typeResult.isValid) return { isValid: false, errors: typeResult.errors, warnings: [] };
    if (!validateForStorage(formData)) return { isValid: false, errors: ['Data cannot be safely stored'], warnings: [] };
    const result = validateQuestionnaireData(formData);
    const consistencyWarnings = checkDataConsistency(formData);
    return {
      isValid: true,
      data: result.data,
      errors: result.errors || [],
      warnings: [...(result.warnings || []), ...consistencyWarnings],
    };
  } catch (err) {
    return { isValid: false, errors: [`Validation failed: ${err.message}`], warnings: [] };
  }
};
